import React from 'react';

export interface HeatmapFilterState {
  sinceDays: number;
  bucket: 'weekly' | 'daily';
  minN: number;
}

export interface HeatmapFiltersProps {
  value: HeatmapFilterState;
  onChange: (next: HeatmapFilterState) => void;
  privacyFloor: number;
}

/**
 * Three-field filter strip above the heatmap (lookback window, time-bucket
 * toggle, min-knocks). Minimum tap target 44px on mobile; keyboard-friendly;
 * min-N enforced client-side against the tenant's privacy floor so users
 * can't drop below the K-anonymity threshold even visually.
 */
export function HeatmapFilters({ value, onChange, privacyFloor }: HeatmapFiltersProps) {
  const set = <K extends keyof HeatmapFilterState>(
    key: K,
    v: HeatmapFilterState[K]
  ) => onChange({ ...value, [key]: v });

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-bg-elevated border border-border-base rounded-lg p-4"
      data-testid="heatmap-filters"
    >
      <div>
        <label
          htmlFor="heatmap-since"
          className="block text-fg-muted text-xs font-semibold uppercase mb-2"
        >
          Lookback (days)
        </label>
        <select
          id="heatmap-since"
          value={value.sinceDays}
          onChange={(e) => set('sinceDays', Number(e.target.value))}
          className="w-full min-h-[44px] bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
        >
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
          <option value={45}>Last 45 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      <div>
        <span className="block text-fg-muted text-xs font-semibold uppercase mb-2">
          Time bucket
        </span>
        <div className="flex gap-2" role="group" aria-label="Time bucket">
          <button
            type="button"
            aria-pressed={value.bucket === 'daily'}
            onClick={() => set('bucket', 'daily')}
            className={`flex-1 min-h-[44px] px-3 py-2 rounded text-sm font-semibold transition ${
              value.bucket === 'daily'
                ? 'bg-accent text-white'
                : 'bg-bg-raised text-fg-secondary hover:bg-bg-elevated'
            }`}
          >
            Daily
          </button>
          <button
            type="button"
            aria-pressed={value.bucket === 'weekly'}
            onClick={() => set('bucket', 'weekly')}
            className={`flex-1 min-h-[44px] px-3 py-2 rounded text-sm font-semibold transition ${
              value.bucket === 'weekly'
                ? 'bg-accent text-white'
                : 'bg-bg-raised text-fg-secondary hover:bg-bg-elevated'
            }`}
          >
            Weekly
          </button>
        </div>
      </div>

      <div>
        <label
          htmlFor="heatmap-min-n"
          className="block text-fg-muted text-xs font-semibold uppercase mb-2"
        >
          Min knocks
        </label>
        <input
          id="heatmap-min-n"
          type="number"
          min={privacyFloor}
          value={value.minN}
          onChange={(e) =>
            set('minN', Math.max(privacyFloor, Number(e.target.value) || privacyFloor))
          }
          className="w-full min-h-[44px] bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
        />
        <p className="text-[11px] text-fg-muted mt-1">
          Can't drop below {privacyFloor} (privacy floor).
        </p>
      </div>

      <div className="flex items-end">
        <button
          type="button"
          onClick={() =>
            onChange({ sinceDays: 45, bucket: 'weekly', minN: privacyFloor })
          }
          className="w-full min-h-[44px] px-3 py-2 rounded bg-bg-raised hover:bg-bg-elevated text-fg-primary text-sm font-medium"
        >
          Reset
        </button>
      </div>
    </div>
  );
}
